import React, { type FC } from 'react';

import { styled } from '@compiled/react';
import FocusRing from '@atlaskit/focus-ring';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { MINIMISED_DROPZONE_CONTAINER_ID } from '@atlassian/jira-issue-create-common-types';
import { JiraBottomRightCornerOutlet } from '@atlassian/jira-layout-controller';
import {
	fireUIAnalytics,
	useAnalyticsEvents,
	fireScreenAnalyticsDeferred,
	MountEvent,
} from '@atlassian/jira-product-analytics-bridge';
import { useEnterFocusState } from '../../../controllers/enter-focus-state';
import { useMinimizableModal, TransitionState } from '../../../controllers/use-minimizable-modal';
import type { MinimizedContainerProps as Props } from '../../../types';

export const AiEnabledMinimized: FC<Props> = ({ children, isItalic, className }) => {
	const { unminimizeModal, previousView, isTransitionInState, setShouldFocusChanger } =
		useMinimizableModal();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { minimizedButtonRef, onKeyDown, wrapperRef } = useEnterFocusState();

	const isViewEntered = isTransitionInState(['ai-enabled-minimized'], TransitionState.ENTERED);

	const unminimize = () => {
		if (!isViewEntered) return;
		setShouldFocusChanger(true);
		unminimizeModal('ai-enabled');
		const analyticsEvent = createAnalyticsEvent({
			action: 'container clicked',
			actionSubject: 'ai-enabled-minimized',
		});
		fireUIAnalytics(analyticsEvent, {
			source: 'globalIssueCreateModal',
			previousGICViewState: previousView,
		});
	};

	return (
		<JiraBottomRightCornerOutlet orderFromRight={3} id="issue-create-minimised-modal">
			<MinimizedOuterWrapper
				id={MINIMISED_DROPZONE_CONTAINER_ID}
				tabIndex={-1}
				ref={wrapperRef}
				data-testid={
					// testid should only be available when view is not transitioning
					// otherwise CY tries to click the view before its last position
					isViewEntered ? 'minimizable-modal.ui.modal-container.minimized-outer-wrapper' : undefined
				}
			>
				<MountEvent onMount={fireScreenAnalyticsDeferred} />
				<FocusRing isInset>
					<MinimizedWrapper
						// @ts-expect-error - TS2322 - Type '((instance: MinimizedContainerProps | null) => void) | MutableRefObject<MinimizedContainerProps | null> | null' is not assignable to type '((string | ((instance: Component<ThemedOuterStyledProps<DiffBetween<{ className: any; }, ClassAttributes<HTMLButtonElement> & ButtonHTMLAttributes<HTMLButtonElement>>, any>, any, any> | null) => void) | RefObject<...>) & (string | ... 1 more ... | RefObject<...>)) | null | undefined'.
						ref={minimizedButtonRef}
						data-testid={
							isViewEntered ? 'minimizable-modal.ui.modal-container.minimized' : undefined
						}
						onKeyDown={onKeyDown}
						onClick={unminimize}
						isItalic={isItalic}
						// eslint-disable-next-line jira/react/no-classname-attribute, @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
						className={className}
					>
						<MinimizedText>{children}</MinimizedText>
					</MinimizedWrapper>
				</FocusRing>
			</MinimizedOuterWrapper>
		</JiraBottomRightCornerOutlet>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MinimizedWrapper = styled.button({
	border: 0,
	boxSizing: 'border-box',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	background: token('elevation.surface.raised', colors.N0),
	height: '48px',
	width: '300px',
	borderRadius: '8px',
	display: 'flex',
	alignItems: 'center',
	padding: `0 ${token('space.200', '16px')}`,
	cursor: 'pointer',
	transition: 'background 0.1s ease-out',
	boxShadow: token(
		'elevation.shadow.overlay',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		`0px 8px 12px 0px ${colors.N40A},
${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766 */ ''}
        0px 0px 1px 0px ${colors.N60A}`,
	),

	'&:hover': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		background: token('elevation.surface.hovered', colors.N20),
		outline: 'none',
	},

	'&:active': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		background: token('elevation.surface.hovered', colors.N20),
		outline: 'none',
	},

	'&:focus:not(:focus-visible)': {
		boxShadow: 'none',
		outline: 'none',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MinimizedText = styled.div({
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text', colors.N600),
	fontSize: '16px',
	fontWeight: 600,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MinimizedOuterWrapper = styled.div({
	'&:focus': {
		outline: 'none',
	},
});
